import { useState, useEffect } from 'react';
import { UserPlus, Loader2, Search, CheckCircle, XCircle, UserCheck, Trash2 } from 'lucide-react';
import { Event, EventAttendee, AttendeeFilters } from '../../../services/EventService';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

interface AttendanceManagerProps {
  eventId: string;
  requiresApproval?: boolean;
}

export function AttendanceManager({ eventId, requiresApproval = false }: AttendanceManagerProps) {
  const { user } = useAuth();
  const [attendees, setAttendees] = useState<EventAttendee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    rsvp: { confirmed: 0, pending: 0, declined: 0 },
    attendance: { attended: 0, noShow: 0, pending: 0 },
    approval: { approved: 0, pending: 0, rejected: 0 }
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAttendees();
    loadStats();
  }, [eventId, statusFilter]);

  const loadAttendees = async () => {
    if (!eventId) return;
    
    setIsLoading(true);
    setError(null);


    try {
      let filters: AttendeeFilters = {};
      
      // Apply status filters
      if (statusFilter === 'confirmed') {
        filters.rsvp_status = 'confirmed';
      } else if (statusFilter === 'pending') {
        filters.rsvp_status = 'pending';
      } else if (statusFilter === 'declined') {
        filters.rsvp_status = 'declined';
      } else if (statusFilter === 'attended') {
        filters.attendance_status = 'attended';
      } else if (statusFilter === 'notAttended') {
        filters.attendance_status = 'pending';
        filters.rsvp_status = 'confirmed';
      }
      
      // Apply search query
      if (searchQuery) {
        filters.search = searchQuery;
      }

      const { data, error } = await Event.getAttendees(eventId, filters);

      if (error) {
        setError('Failed to load attendees');
        console.error('Error loading attendees:', error);
        return;
      }

      setAttendees(data || []);
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    if (!eventId) return;
    
    try {
      const stats = await Event.getAttendanceStats(eventId);
      if (!stats.error) {
        setStats(stats);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !user?.id) return;
    
    setIsSending(true);
    
    try {
      const { data, error } = await Event.inviteAttendee(
        eventId,
        { email: inviteEmail.trim() },
        user.id
      );
      
      if (error) {
        alert(error.message || 'Failed to send invitation');
      } else {
        setInviteEmail('');
        loadAttendees();
        loadStats();
      }
    } catch (err) {
      console.error('Error inviting attendee:', err);
      alert('An unexpected error occurred');
    } finally {
      setIsSending(false);
    }
  };

  const handleUpdateRsvp = async (attendeeId: string, status: 'confirmed' | 'declined') => {
    try {
      const { error } = await Event.updateAttendeeRsvp(attendeeId, status);
      if (error) {
        alert('Failed to update RSVP status');
        return;
      }
      
      loadAttendees();
      loadStats();
    } catch (err) {
      console.error('Error updating RSVP status:', err);
      alert('An unexpected error occurred');
    }
  };

  const handleApproval = async (attendeeId: string, status: 'approved' | 'rejected') => {
    if (!user?.id) return;
    
    try {
      const { error } = await Event.approveAttendee(attendeeId, status, user.id);
      if (error) {
        alert('Failed to update approval status');
        return;
      }
      
      loadAttendees();
      loadStats();
    } catch (err) {
      console.error('Error updating approval status:', err);
      alert('An unexpected error occurred');
    }
  };

  const handleAttendance = async (attendeeId: string, status: 'attended' | 'no-show') => {
    try {
      const { error } = await Event.markAttendance(attendeeId, status);
      if (error) {
        alert('Failed to update attendance status');
        return;
      }
      
      loadAttendees();
      loadStats();
    } catch (err) {
      console.error('Error updating attendance status:', err);
      alert('An unexpected error occurred');
    }
  };

  const handleSearch = () => {
    loadAttendees();
  };

  const handleRemoveAttendee = async (attendeeId: string) => {
    try {
      setLoading(true);
      const { error } = await Event.removeAttendee(attendeeId, user?.id || '');
      
      if (error) {
        console.error('Error removing attendee:', error);
        alert('Error removing attendee: ' + error.message);
        return;
      }

      // Refresh the attendees list
      await loadAttendees();
      
    } catch (error) {
      console.error('Error removing attendee:', error);
      alert('An unexpected error occurred while removing the attendee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick stats */}
      

      {/* Invitation and search form */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-white p-4 rounded-lg border mb-6 shadow-sm">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 mb-3">Invite Helper</h3>
          <div className="flex space-x-2">
            <Input
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={handleInvite}
              disabled={isSending || !inviteEmail.trim()}
              variant="primary"
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              <span className="ml-2">Invite</span>
            </Button>
          </div>
        </div>

        <div className="flex-1">
          <h3 className="font-medium text-gray-900 mb-3">Filter Helpers</h3>
          <div className="flex space-x-2">
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder="Search helpers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
              <Search className="h-4 w-4 absolute left-2.5 top-3 text-gray-400" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All helpers</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="pending">Pending RSVP</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="attended">Checked in</SelectItem>
                <SelectItem value="notAttended">Not checked in</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Attendees list */}
      <div className="bg-white rounded-lg border shadow">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-600">{error}</div>
        ) : attendees.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No helper's found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Name</th>
                  <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Email</th>
                  <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Registration Date</th>
                  <th className="text-right py-2 px-4 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {attendees.map((attendee) => (
                  <tr key={attendee.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-purple-100 rounded-full mr-2 flex items-center justify-center overflow-hidden">
                          {attendee.avatar_url ? (
                            <img
                              src={attendee.avatar_url}
                              alt={attendee.name || 'Helper'}
                              className="w-8 h-8 object-cover"
                            />
                          ) : (
                            <span className="text-xs font-medium text-purple-600">
                              {(attendee.name || attendee.email || 'A').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <span>{attendee.name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{attendee.email}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col space-y-1">
                        <span
                          className={`inline-block px-2 py-1 text-xs rounded-full ${
                            attendee.rsvp_status === 'confirmed'
                              ? 'bg-green-100 text-green-700'
                              : attendee.rsvp_status === 'declined'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {attendee.rsvp_status === 'confirmed' ? 'Confirmed' : 
                           attendee.rsvp_status === 'declined' ? 'Declined' : 'Pending'}
                        </span>
                      
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {new Date(attendee.registered_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end items-center space-x-1">
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveAttendee(attendee.id)}
                          className="h-8 w-8 text-red-600 hover:text-red-700"
                          disabled={loading}
                          title="Remove attendee"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}